import { useState, useRef, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import SignatureCanvas from 'react-signature-canvas'
import axios from 'axios'
import Card from '../../components/common/Card'
import Button from '../../components/common/Button'
import Spinner from '../../components/common/Spinner'
import PDFViewer from '../../components/common/PDFViewer'
import {
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  PaperClipIcon
} from '@heroicons/react/24/outline'

/**
 * Public Contract Signing Page
 * Accessible without authentication via unique token
 */
export default function SignContract() {
  const { token } = useParams()
  const navigate = useNavigate()
  const sigPadRef = useRef(null)
  
  const [contract, setContract] = useState(null)
  const [trainer, setTrainer] = useState(null)
  const [attachments, setAttachments] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [signatureData, setSignatureData] = useState(null)
  const [isSigned, setIsSigned] = useState(false)
  const [agreedToTerms, setAgreedToTerms] = useState(false)

  // Fetch contract details by token
  useEffect(() => {
    const fetchContract = async () => {
      try {
        const fullUrl = `${import.meta.env.VITE_API_URL}/sign/${token}`
        console.log('[CLIENT DEBUG] VITE_API_URL:', import.meta.env.VITE_API_URL)
        console.log('[CLIENT DEBUG] Full sign URL:', fullUrl)
        console.log('[CLIENT DEBUG] Fetching contract for token:', token)
        
        const response = await axios.get(fullUrl)
        console.log('[CLIENT DEBUG] Response received:', response.status)
        console.log('[CLIENT DEBUG] Response data:', response.data)
        
        setContract(response.data.contract)
        setTrainer(response.data.trainer)
        setAttachments(response.data.attachments || [])
        setLoading(false)
        
        // Store signature token in localStorage for PDF/attachment access
        if (token) {
          localStorage.setItem('signatureToken', token)
        }
      } catch (err) {
        console.error('[CLIENT DEBUG] Error fetching contract:', err)
        console.error('[CLIENT DEBUG] Error response:', err.response)
        console.error('[CLIENT DEBUG] Error message:', err.message)
        setError(err.response?.data?.error || err.response?.data?.message || 'Invalid or expired signing link')
        setLoading(false)
      }
    }

    if (token) {
      fetchContract()
    }
    
    // Cleanup: remove signature token when component unmounts
    return () => {
      localStorage.removeItem('signatureToken')
    }
  }, [token])

  // Sign contract mutation
  const signMutation = useMutation({
    mutationFn: async (signature) => {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/sign/${token}`,
        { signature_blob: signature }
      )
      return response.data
    },
    onSuccess: () => {
      setIsSigned(true)
    },
    onError: (error) => {
      alert(error.response?.data?.message || 'Failed to sign contract')
    },
  })

  const handleClear = () => {
    sigPadRef.current?.clear()
    setSignatureData(null)
  }

  const handleSign = () => {
    if (!agreedToTerms) {
      alert('Please confirm that you have read and agree to the contract terms')
      return
    }

    if (sigPadRef.current?.isEmpty()) {
      alert('Please provide a signature')
      return
    }

    const signature = sigPadRef.current.toDataURL()
    setSignatureData(signature)
    signMutation.mutate(signature)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <Card.Body className="text-center py-12">
            <XCircleIcon className="mx-auto h-16 w-16 text-red-500 mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Invalid Link</h1>
            <p className="text-gray-600">{error}</p>
            <p className="text-sm text-gray-500 mt-4">
              Please contact the administrator if you believe this is an error.
            </p>
          </Card.Body>
        </Card>
      </div>
    )
  }

  // Defensive check to prevent rendering when data is not yet loaded
  if (!trainer || !contract) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  if (isSigned) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full">
          <Card.Body className="text-center py-12">
            <CheckCircleIcon className="mx-auto h-16 w-16 text-green-500 mb-4" />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Contract Signed Successfully!</h1>
            <p className="text-gray-600 mb-8">
              Thank you for signing the contract. You will receive a confirmation email shortly.
            </p>
            
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <dl className="space-y-3 text-left">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Contract</dt>
                  <dd className="mt-1 text-sm text-gray-900">{contract.title}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Signed By</dt>
                  <dd className="mt-1 text-sm text-gray-900">{trainer.first_name} {trainer.last_name}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Signed At</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {new Date().toLocaleString()}
                  </dd>
                </div>
              </dl>
            </div>

            {signatureData && (
              <div className="mb-6">
                <p className="text-sm font-medium text-gray-700 mb-2">Your Signature:</p>
                <img 
                  src={signatureData} 
                  alt="Signature" 
                  className="mx-auto border-2 border-gray-300 rounded-lg bg-white p-2"
                  style={{ maxWidth: '300px' }}
                />
              </div>
            )}

            <p className="text-xs text-gray-500">
              You can now close this window.
            </p>
          </Card.Body>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-primary-600 mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Sign Contract</h1>
          <p className="text-gray-600">
            Please review the contract details and provide your signature below
          </p>
        </div>

        {/* Trainer Info */}
        <Card>
          <Card.Header>
            <h2 className="text-lg font-semibold text-gray-900">Your Information</h2>
          </Card.Header>
          <Card.Body>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Name</dt>
                <dd className="mt-1 text-sm text-gray-900">{trainer.first_name} {trainer.last_name}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Email</dt>
                <dd className="mt-1 text-sm text-gray-900">{trainer.email}</dd>
              </div>
            </dl>
          </Card.Body>
        </Card>

        {/* Contract Details */}
        <Card>
          <Card.Header>
            <h2 className="text-lg font-semibold text-gray-900">Contract Details</h2>
          </Card.Header>
          <Card.Body>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Contract Title</dt>
                <dd className="mt-1 text-sm text-gray-900">{contract.title}</dd>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Start Date</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {new Date(contract.start_date).toLocaleDateString()}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">End Date</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {new Date(contract.end_date).toLocaleDateString()}
                  </dd>
                </div>
              </div>

              {contract.total_amount && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Total Amount</dt>
                    <dd className="mt-1 text-lg font-semibold text-gray-900">
                      €{contract.total_amount.toLocaleString()}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Payment Frequency</dt>
                    <dd className="mt-1 text-sm text-gray-900 capitalize">
                      {contract.payment_frequency}
                    </dd>
                  </div>
                </div>
              )}

            </dl>
          </Card.Body>
        </Card>

        {/* Contract PDF Viewer */}
        {contract.contract_path && (
          <Card>
            <Card.Header>
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Contract Document</h2>
                <a
                  href={`${import.meta.env.VITE_API_URL}/contracts/${contract.id}/download`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm"
                >
                  <Button variant="secondary" size="sm">
                    <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                    Download PDF
                  </Button>
                </a>
              </div>
            </Card.Header>
            <Card.Body>
              <PDFViewer
                url={`${import.meta.env.VITE_API_URL}/contracts/${contract.id}/download`}
                signatureToken={token}
              />
            </Card.Body>
          </Card>
        )}

        {/* Attachments */}
        {attachments && attachments.length > 0 && (
          <Card>
            <Card.Header>
              <h2 className="text-lg font-semibold text-gray-900">Contract Documents</h2>
            </Card.Header>
            <Card.Body>
              <div className="space-y-3">
                {attachments.map((attachment) => (
                  <div
                    key={attachment.id}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <DocumentTextIcon className="h-6 w-6 text-gray-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {attachment.file_name}
                      </p>
                      {attachment.description && (
                        <p className="text-xs text-gray-600 mt-1">{attachment.description}</p>
                      )}
                      {attachment.file_size && (
                        <p className="text-xs text-gray-500 mt-1">
                          {(attachment.file_size / 1024).toFixed(2)} KB
                        </p>
                      )}
                    </div>
                    <a
                      href={`${import.meta.env.VITE_API_URL}/api/attachments/${attachment.id}/download`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-shrink-0"
                    >
                      <Button variant="secondary" size="sm">
                        <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                    </a>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-4 italic">
                <PaperClipIcon className="h-3 w-3 inline mr-1" />
                {attachments.length} additional document{attachments.length !== 1 ? 's' : ''} available
              </p>
            </Card.Body>
          </Card>
        )}

        {/* Signature Pad */}
        <Card>
          <Card.Header>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Your Signature</h2>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleClear}
              >
                <ArrowPathIcon className="h-4 w-4 mr-1" />
                Clear
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            <div className="space-y-4">
              <div className="border-2 border-gray-300 rounded-lg bg-white">
                <SignatureCanvas
                  ref={sigPadRef}
                  canvasProps={{
                    className: 'w-full h-48 cursor-crosshair',
                  }}
                  backgroundColor="rgb(255, 255, 255)"
                />
              </div>
              
              <p className="text-xs text-gray-500 text-center">
                Draw your signature above using your mouse or touch screen
              </p>

              {/* Terms Agreement Checkbox */}
              <div className="border-2 border-primary-200 bg-primary-50 rounded-lg p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-1 h-5 w-5 text-primary-600 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-900 flex-1">
                    <strong>I have read the contract and agree to the terms and conditions outlined in the contract.</strong>
                    <br />
                    <span className="text-gray-700">I understand that this signature is legally binding.</span>
                  </span>
                </label>
              </div>
            </div>
          </Card.Body>
          <Card.Footer>
            <div className="flex justify-end">
              <Button
                variant="primary"
                size="lg"
                onClick={handleSign}
                loading={signMutation.isPending}
                disabled={!agreedToTerms}
              >
                <CheckCircleIcon className="h-5 w-5 mr-2" />
                Sign Contract
              </Button>
            </div>
          </Card.Footer>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-gray-500">
          If you have any questions about this contract, please contact the administrator.
        </p>
      </div>
    </div>
  )
}